/**
 * A leitura do documento da saída para a mesa (E202.F1, GUS-145, ADR-0145 §1).
 *
 * - A primeira leitura mostra o esqueleto; a falha dela tem "Tentar de novo" (`FalhaComSaida`).
 * - A releitura (a mesa gravou, outra saída) mantém o documento anterior à vista, com "Atualizando o
 *   documento…", até a resposta nova chegar; se ela falhar, o anterior fica e a falha aparece.
 * - Toda leitura leva um número de ordem (como `useEditorDaSaida`, E200.F3): a resposta antiga que
 *   chega depois de uma mais nova não entra no estado.
 *
 * E206.F3 (GUS-296 item 4, QA de 01/11: GT-2026-0324 e 0326 abriram com "Não foi possível montar o
 * documento"): na primeira abertura o servidor monta o documento enquanto a preparação do pacote
 * roda. A primeira leitura que falha tenta de novo sozinha (`ESPERAS_DA_PRIMEIRA_LEITURA_MS`), com a
 * mesa dizendo que o documento está sendo preparado; só esgotadas as tentativas a falha aparece, com
 * o motivo (`falha`: o prazo ou o servidor). A releitura que falha segue como antes.
 */
import type { AxiosInstance } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CorrecaoDoRascunhoError, type SaidaDaEdicao } from '@/lib/correcaoDoRascunho';
import { lerDocumentoDaSaida, type DocumentoDaSaidaData } from '@/lib/documentoDaSaida';

/** As esperas antes de cada nova tentativa automática da primeira leitura. */
export const ESPERAS_DA_PRIMEIRA_LEITURA_MS: readonly number[] = [3_000, 8_000, 15_000];

/** Por que a leitura falhou: o prazo esgotado ou o servidor que não respondeu. */
export type FalhaDaLeitura = 'tempo' | 'indisponivel';

export interface LeituraDoDocumento {
  /** O último documento lido da saída pedida; `null` até a primeira resposta. */
  dados: DocumentoDaSaidaData | null;
  carregando: boolean;
  falhou: boolean;
  /** E206.F3: o motivo da falha que aparece; `null` sem falha. */
  falha?: FalhaDaLeitura | null;
  /** E206.F3: a primeira leitura falhou e vai tentar de novo sozinha. */
  esperando?: boolean;
  tentarDeNovo: () => void;
}

function falhaDe(err: unknown): FalhaDaLeitura {
  return err instanceof CorrecaoDoRascunhoError && err.motivo === 'pedido_tempo'
    ? 'tempo'
    : 'indisponivel';
}

export function useDocumentoDaSaida(
  client: AxiosInstance,
  caseId: string,
  saida: SaidaDaEdicao,
  releitura?: unknown,
): LeituraDoDocumento {
  const [dados, setDados] = useState<DocumentoDaSaidaData | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [falha, setFalha] = useState<FalhaDaLeitura | null>(null);
  const [esperando, setEsperando] = useState(false);
  const [tentativa, setTentativa] = useState(0);
  const ultima = useRef(0);
  // A primeira leitura de cada caso e saída: quantas novas tentativas automáticas já foram.
  const automaticas = useRef({ chave: '', feitas: 0 });
  const lido = useRef('');

  useEffect(() => {
    const ordem = ++ultima.current;
    const chave = `${caseId}:${saida}`;
    if (automaticas.current.chave !== chave) automaticas.current = { chave, feitas: 0 };
    let espera: number | undefined;
    setCarregando(true);
    setFalha(null);
    lerDocumentoDaSaida(client, caseId, saida)
      .then((documento) => {
        if (ordem !== ultima.current) return;
        lido.current = chave;
        setDados(documento);
        setEsperando(false);
        setCarregando(false);
      })
      .catch((err: unknown) => {
        if (ordem !== ultima.current) return;
        const atraso =
          lido.current === chave
            ? undefined
            : ESPERAS_DA_PRIMEIRA_LEITURA_MS[automaticas.current.feitas];
        if (atraso !== undefined) {
          automaticas.current.feitas += 1;
          setEsperando(true);
          espera = window.setTimeout(() => setTentativa((n) => n + 1), atraso);
          return;
        }
        setEsperando(false);
        setFalha(falhaDe(err));
        setCarregando(false);
      });
    return () => {
      if (espera !== undefined) window.clearTimeout(espera);
    };
  }, [client, caseId, saida, releitura, tentativa]);

  // Outra saída: o documento da anterior não fica à vista enquanto a nova é lida.
  const visto = dados !== null && dados.saida === saida && dados.case_id === caseId ? dados : null;
  const tentarDeNovo = useCallback(() => setTentativa((n) => n + 1), []);
  return { dados: visto, carregando, falhou: falha !== null, falha, esperando, tentarDeNovo };
}
